import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import {
  fetchBlog,
  updateBlog,
  uploadBlogCover,
  uploadBlogAsset,
  getBlogAssets,
  fetchUsers,
  fetchSkills,
  type Blog,
  type BlogUpdate,
  type User,
  type Skill,
} from '@/admin/lib/api';
import { Badge } from '@/admin/components/ui/badge';
import { Button } from '@/admin/components/ui/button';
import { Skeleton } from '@/admin/components/ui/skeleton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/admin/components/ui/alert-dialog';
import { IconArrowLeft, IconCheck, IconFile, IconX } from '@tabler/icons-react';
import { useUnsavedChanges } from '@/admin/hooks/use-unsaved-changes';
import { CollapsibleSection } from '@/admin/components/skill/collapsible-section';
import { SkillSelectorWithPreview } from '@/admin/components/skill/skill-selector-with-preview';

// Components
import { BlogBasicInfoSection } from '@/admin/components/blog/blog-basic-info-section';
import { BlogContentSection } from '@/admin/components/blog/blog-content-section';
import { BlogSettingsSidebar } from '@/admin/components/blog/blog-settings-sidebar';
import { SeoSection } from '@/admin/components/skill/seo-section';

export interface BlogFormData {
  title: string;
  slug: string;
  status: string;
  author_id: string;
  content: string;
  seo_title: string;
  seo_description: string;
  seo_image: string;
  seo_keywords: string;
  canonical_url: string;
}

export const BlogDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [blog, setBlog] = useState<Blog | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [skills, setSkills] = useState<Skill[]>([]);
  const [loadingSkills, setLoadingSkills] = useState(false);
  const [selectedSkillIds, setSelectedSkillIds] = useState<string[]>([]);

  // Section saving states
  const [savingContent, setSavingContent] = useState(false);
  const [savingSeo, setSavingSeo] = useState(false);
  const [uploadingMedia, setUploadingMedia] = useState(false);

  const form = useForm<BlogFormData>({
    defaultValues: {
      title: '',
      slug: '',
      status: 'draft',
      author_id: '',
      content: '',
      seo_title: '',
      seo_description: '',
      seo_image: '',
      seo_keywords: '',
      canonical_url: '',
    },
  });
  const { control, reset, formState, getValues, watch, setValue } = form;
  const { isDirty } = formState;
  const { isBlocked, confirmNavigation, cancelNavigation } =
    useUnsavedChanges(isDirty);

  const loadUsers = useCallback(async () => {
    setLoadingUsers(true);
    try {
      const result = await fetchUsers(100, 0);
      setUsers(result.users || []);
    } catch (err) {
      console.error('Failed to load users:', err);
    } finally {
      setLoadingUsers(false);
    }
  }, []);

  const loadSkills = useCallback(async () => {
    setLoadingSkills(true);
    try {
      const result = await fetchSkills(100, 0);
      setSkills(result.skills || []);
    } catch (err) {
      console.error('Failed to load skills:', err);
    } finally {
      setLoadingSkills(false);
    }
  }, []);

  const loadBlog = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      const data = await fetchBlog(id);
      setBlog(data);
      setSelectedSkillIds(data.skill_ids || []);
      reset({
        title: data.title || '',
        slug: data.slug || '',
        status: data.status || 'draft',
        author_id: data.author_id || '',
        content: data.content || '',
        seo_title: data.seo_title || '',
        seo_description: data.seo_description || '',
        seo_image: data.seo_image || '',
        seo_keywords: data.seo_keywords?.join(', ') || '',
        canonical_url: data.canonical_url || '',
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load blog');
    } finally {
      setLoading(false);
    }
  }, [id, reset]);

  useEffect(() => {
    if (id) {
      loadBlog();
      loadUsers();
      loadSkills();
    }
  }, [id, loadBlog, loadUsers, loadSkills]);

  const handleSaveAll = async () => {
    if (!id) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const values = getValues();
      const updateData: BlogUpdate = {
        title: values.title || undefined,
        slug: values.slug || undefined,
        status: values.status,
        author_id: values.author_id || undefined,
        content: values.content || undefined,
        seo_title: values.seo_title || undefined,
        seo_description: values.seo_description || undefined,
        seo_image: values.seo_image || undefined,
        seo_keywords: values.seo_keywords
          ? values.seo_keywords
              .split(',')
              .map(k => k.trim())
              .filter(Boolean)
          : undefined,
        canonical_url: values.canonical_url || undefined,
        skill_ids: selectedSkillIds,
      };
      const updated = await updateBlog(id, updateData);
      setBlog(updated);
      reset(values);
      setSuccess('Saved');
      setTimeout(() => setSuccess(null), 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveContent = async () => {
    if (!id) return;
    setSavingContent(true);
    try {
      const content = getValues('content');
      await updateBlog(id, { content });
      reset({ ...getValues() }, { keepValues: true });
      setSuccess('Content saved');
      setTimeout(() => setSuccess(null), 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save content');
    } finally {
      setSavingContent(false);
    }
  };

  const handleSaveSeo = async () => {
    if (!id) return;
    setSavingSeo(true);
    try {
      const values = getValues();
      await updateBlog(id, {
        seo_title: values.seo_title || undefined,
        seo_description: values.seo_description || undefined,
        seo_image: values.seo_image || undefined,
        seo_keywords: values.seo_keywords
          ? values.seo_keywords
              .split(',')
              .map(k => k.trim())
              .filter(Boolean)
          : undefined,
        canonical_url: values.canonical_url || undefined,
      });
      reset({ ...getValues() }, { keepValues: true });
      setSuccess('SEO saved');
      setTimeout(() => setSuccess(null), 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save SEO');
    } finally {
      setSavingSeo(false);
    }
  };

  const handleCoverUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!id || !e.target.files?.length) return;
    const file = e.target.files[0];
    setUploadingCover(true);
    setError(null);
    try {
      const result = await uploadBlogCover(id, file);
      setBlog(prev =>
        prev
          ? { ...prev, cover_image_url: result.url, cover_image: result.path }
          : null
      );
      setSuccess('Cover image updated');
      setTimeout(() => setSuccess(null), 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload cover');
    } finally {
      setUploadingCover(false);
    }
  };

  const handleMediaUpload = useCallback(
    async (files: File[]): Promise<{ key: string }[]> => {
      if (!id || files.length === 0) return [];
      setUploadingMedia(true);
      setError(null);
      const uploadedKeys: { key: string }[] = [];
      try {
        for (const file of files) {
          const result = await uploadBlogAsset(id, file);
          uploadedKeys.push({ key: result.key });
        }
        setSuccess(`Uploaded ${files.length} file(s)`);
        setTimeout(() => setSuccess(null), 2000);
        // Refresh only assets mapping (not the whole blog, which would reset form)
        const newAssets = await getBlogAssets(id);
        setBlog(prev => (prev ? { ...prev, assets: newAssets } : null));
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to upload media');
      } finally {
        setUploadingMedia(false);
      }
      return uploadedKeys;
    },
    [id]
  );

  const handleSkillToggle = (skillId: string) => {
    setSelectedSkillIds(prev =>
      prev.includes(skillId)
        ? prev.filter(id => id !== skillId)
        : [...prev, skillId]
    );
  };

  const handleStatusChange = async (status: string) => {
    if (!id) return;
    try {
      setValue('status', status);
      await updateBlog(id, { status });
      setBlog(prev => (prev ? { ...prev, status } : null));
      setSuccess('Status updated');
      setTimeout(() => setSuccess(null), 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update status');
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'published':
        return (
          <Badge className="bg-green-500 hover:bg-green-600">Published</Badge>
        );
      case 'archived':
        return <Badge variant="secondary">Archived</Badge>;
      default:
        return <Badge variant="outline">Draft</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col h-full bg-background">
        <div className="flex items-center justify-between px-6 py-3 border-b bg-card">
          <div className="flex items-center gap-4">
            <Skeleton className="h-8 w-20" />
            <Skeleton className="h-5 w-32" />
          </div>
          <Skeleton className="h-9 w-28" />
        </div>
        <div className="flex flex-1 overflow-hidden">
          <div className="flex-1 p-6">
            <Skeleton className="h-10 w-80 mb-4" />
            <Skeleton className="h-[400px] w-full" />
          </div>
          <div className="w-64 border-l p-6 space-y-4">
            <Skeleton className="h-6 w-32" />
            <Skeleton className="h-32 w-full" />
          </div>
        </div>
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="flex flex-col h-full items-center justify-center bg-background">
        <div className="text-center p-8">
          <div className="w-16 h-16 rounded-full bg-muted mx-auto mb-4 flex items-center justify-center">
            <IconFile className="h-8 w-8 text-muted-foreground" />
          </div>
          <h2 className="text-lg font-medium mb-1">Blog not found</h2>
          <p className="text-sm text-muted-foreground mb-4">
            The blog you&apos;re looking for doesn&apos;t exist.
          </p>
          <Button variant="outline" onClick={() => navigate('/blogs')}>
            <IconArrowLeft className="h-4 w-4 mr-2" />
            Back to Blogs
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-3 border-b bg-card shrink-0">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            className="-ml-2 h-8 text-muted-foreground hover:text-foreground"
            onClick={() => navigate('/blogs')}
          >
            <IconArrowLeft className="h-4 w-4 mr-1" />
            Blogs
          </Button>
          <span className="text-muted-foreground/50">/</span>
          <div className="flex items-center gap-2">
            <h1 className="text-base font-medium">{blog.title}</h1>
            {getStatusBadge(blog.status)}
            {isDirty && (
              <span className="text-xs text-amber-600 dark:text-amber-400">
                (unsaved)
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          {success && (
            <span className="text-xs text-emerald-600 dark:text-emerald-400 flex items-center gap-1 bg-emerald-500/10 px-2 py-1 rounded">
              <IconCheck className="h-3 w-3" /> {success}
            </span>
          )}
          {error && (
            <span className="text-xs text-destructive flex items-center gap-1 bg-destructive/10 px-2 py-1 rounded max-w-xs truncate">
              <IconX className="h-3 w-3 shrink-0" /> {error}
            </span>
          )}
          <Button
            size="sm"
            onClick={handleSaveAll}
            disabled={saving || !isDirty}
          >
            {saving ? 'Saving...' : 'Save All'}
          </Button>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden min-h-0">
        {/* Main Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-4">
            {/* Basic Info & Cover */}
            <BlogBasicInfoSection
              control={control}
              users={users}
              loadingUsers={loadingUsers}
              coverImageUrl={blog.cover_image_url}
              onCoverUpload={handleCoverUpload}
              uploadingCover={uploadingCover}
            />

            {/* Content */}
            <BlogContentSection
              control={control}
              onSaveContent={handleSaveContent}
              savingContent={savingContent}
              onUploadMedia={handleMediaUpload}
              uploadingMedia={uploadingMedia}
              skills={skills}
              loadingSkills={loadingSkills}
              assetsMapping={blog.assets}
            />

            {/* SEO */}
            <SeoSection
              control={control}
              onSaveSeo={handleSaveSeo}
              savingSeo={savingSeo}
              defaultTitle={blog.title}
              defaultDescription=""
              defaultImage={blog.cover_image_url || ''}
              itemName={blog.slug}
              urlPathPrefix="blog"
            />

            {/* Related Skills */}
            <CollapsibleSection
              id="related-skills"
              title="Related Skills"
              description="Associate skills with this blog post. Hover over a skill to preview."
              defaultOpen={false}
            >
              <SkillSelectorWithPreview
                skills={skills}
                selectedIds={selectedSkillIds}
                onToggle={handleSkillToggle}
                loading={loadingSkills}
              />
            </CollapsibleSection>
          </div>
        </div>

        {/* Right Sidebar */}
        <BlogSettingsSidebar
          blog={blog}
          watch={watch}
          onStatusChange={handleStatusChange}
          loading={loading}
        />
      </div>

      {/* Unsaved Changes Dialog */}
      <AlertDialog open={isBlocked} onOpenChange={() => cancelNavigation()}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. Are you sure you want to leave?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={cancelNavigation}>
              Stay
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmNavigation}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Leave
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
